import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService } from '../finance/finance-api.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  ProofOfVisitRecord,
  RetailAuditRecord,
  SalesApiService,
  SalesOrderListItem,
  VisitPlanRecord
} from './sales-api.service';

type VisitTab = 'execution' | 'proofs' | 'retail';

@Component({
  standalone: true,
  selector: 'ain-sales-distribution-visit-detail',
  imports: [
    AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule
  ],
  template: `
    <ain-page [title]="t('sales.distribution.visits.form.title')" [description]="t('sales.distribution.visits.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else if (visit()) {
        <header class="card">
          @if (visit()!.routePlanId) {
            <a class="back-link" [routerLink]="['/sales/distribution-routes', visit()!.routePlanId]">{{ t('sales.distribution.visits.backToRoute') }}</a>
          }
        </header>
        <section class="card summary">
          <article><span>{{ t('sales.distribution.visits.fields.sequence') }}</span><strong>{{ visit()!.sequenceNumber }}</strong></article>
          <article><span>{{ t('sales.distribution.visits.fields.customer') }}</span><strong>{{ customerLabel(visit()!.customerAccountId) }}</strong></article>
          <article><span>{{ t('columns.status') }}</span><strong>{{ visitStatusLabel(visit()!.status) }}</strong></article>
        </section>
        <section class="card actions-row">
          <button
            pButton
            type="button"
            [label]="t('sales.distribution.visits.action.createOrder')"
            (click)="openCreateOrder()"
          ></button>
        </section>
        @if (linkedOrders().length > 0) {
          <section class="card">
            <h3>{{ t('sales.distribution.visits.linkedOrders') }}</h3>
            <p-table [value]="linkedOrders()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.orders.fields.orderNumber') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.orderNumber || '-' }}</td>
                  <td>{{ row.status }}</td>
                  <td><a [routerLink]="['/sales/orders', row.id]">{{ t('common.open') }}</a></td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
        <div class="tabs">
          <button type="button" [class.active]="activeTab()==='execution'" (click)="setTab('execution')">{{ t('sales.distribution.visits.tabs.execution') }}</button>
          <button type="button" [class.active]="activeTab()==='proofs'" (click)="setTab('proofs')">{{ t('sales.distribution.visits.tabs.proofs') }}</button>
          <button type="button" [class.active]="activeTab()==='retail'" (click)="setTab('retail')">{{ t('sales.distribution.visits.tabs.retail') }}</button>
        </div>
        @if (activeTab()==='execution') {
          @if (visit()!.execution) {
            <section class="card summary">
              <article><span>{{ t('sales.distribution.visits.fields.executionStatus') }}</span><strong>{{ executionStatusLabel(visit()!.execution!.status) }}</strong></article>
              <article><span>{{ t('sales.distribution.visits.fields.startedAt') }}</span><strong>{{ visit()!.execution!.startedAtUtc ? (visit()!.execution!.startedAtUtc | ainDate) : '-' }}</strong></article>
            </section>
          }
          <section class="card grid">
            <label><span>{{ t('sales.distribution.visits.fields.outcomeNotes') }}</span><input pInputText [(ngModel)]="outcomeNotes" /></label>
            <div class="actions">
              @if (canStart()) { <button pButton type="button" [label]="t('sales.distribution.visits.start')" (click)="start()"></button> }
              @if (canComplete()) { <button pButton type="button" [label]="t('sales.distribution.visits.complete')" (click)="complete()"></button> }
            </div>
          </section>
        }
        @if (activeTab()==='proofs') {
          <section class="card grid">
            <label><span>{{ t('sales.distribution.proofs.fields.notes') }}</span><input pInputText [(ngModel)]="proofNotes" /></label>
            <button pButton type="button" [label]="t('sales.distribution.proofs.captureVisit')" (click)="captureProof()"></button>
          </section>
          <p-table [value]="proofs()">
            <ng-template pTemplate="header"><tr><th>{{ t('sales.distribution.proofs.fields.capturedAt') }}</th><th>{{ t('sales.distribution.proofs.fields.hash') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.capturedAtUtc | ainDate }}</td><td class="mono">{{ row.contentHash }}</td></tr></ng-template>
          </p-table>
        }
        @if (activeTab()==='retail') {
          <section class="card grid">
            <label>
              <span>{{ t('sales.distribution.retail.fields.auditType') }}</span>
              <p-select [options]="auditTypes" optionValue="value" [(ngModel)]="auditForm.auditType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('sales.distribution.retail.fields.compliance') }}</span><p-inputNumber [(ngModel)]="auditForm.complianceScore" [min]="0" [max]="100" /></label>
            <label><span>{{ t('sales.distribution.retail.fields.notes') }}</span><input pInputText [(ngModel)]="auditForm.notes" /></label>
            <button pButton type="button" [label]="t('sales.distribution.retail.submit')" (click)="submitAudit()"></button>
          </section>
          <p-table [value]="audits()">
            <ng-template pTemplate="header"><tr><th>{{ t('sales.distribution.retail.fields.auditType') }}</th><th>{{ t('sales.distribution.retail.fields.compliance') }}</th><th>{{ t('columns.status') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.auditType }}</td><td>{{ row.complianceScore ?? '-' }}</td><td>{{ row.status }}</td></tr></ng-template>
          </p-table>
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none}
    .summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px}
    article{display:grid;gap:4px}
    article span{color:#64748b;font-size:12px}
    .grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    label{display:grid;gap:6px}
    .actions{display:flex;gap:8px}
    .actions-row{display:flex;gap:8px;flex-wrap:wrap}
    .tabs{display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap}
    .tabs button{border:1px solid #cbd5e1;border-radius:999px;padding:6px 12px;cursor:pointer;background:#fff}
    .tabs button.active{background:#1d4ed8;color:#fff}
    .mono{font-family:monospace;font-size:12px}
    .error{color:#b91c1c}
    h3{margin:0 0 8px;font-size:14px}
  `]
})
export class SalesDistributionVisitDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly platformApi = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly errorMessage = signal('');
  readonly visit = signal<VisitPlanRecord | null>(null);
  readonly proofs = signal<ProofOfVisitRecord[]>([]);
  readonly audits = signal<RetailAuditRecord[]>([]);
  readonly linkedOrders = signal<SalesOrderListItem[]>([]);
  readonly customerNamesById = signal<Record<string, string>>({});
  readonly activeTab = signal<VisitTab>('execution');
  outcomeNotes = '';
  proofNotes = '';
  readonly auditTypes = [
    { value: 'Shelf', labelKey: 'sales.distribution.retail.types.shelf' },
    { value: 'CompetitorPrice', labelKey: 'sales.distribution.retail.types.competitor' },
    { value: 'StoreCheck', labelKey: 'sales.distribution.retail.types.storeCheck' }
  ];
  auditForm = { auditType: 'Shelf', complianceScore: 80, notes: '' };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('visitId');
    if (!id) { this.loading.set(false); return; }
    this.loadCustomerNames();
    this.load(id);
  }

  openCreateOrder(): void {
    const visit = this.visit();
    if (!visit) {
      return;
    }

    void this.router.navigate(['/sales/orders/new'], {
      queryParams: {
        customerAccountId: visit.customerAccountId,
        visitPlanId: visit.id
      }
    });
  }

  setTab(tab: VisitTab): void {
    this.activeTab.set(tab);
    const id = this.visit()?.id;
    if (!id) return;
    if (tab === 'proofs') this.api.listProofsOfVisit(id).subscribe({ next: p => this.proofs.set(p) });
    if (tab === 'retail') this.api.listRetailAudits(id).subscribe({ next: a => this.audits.set(a) });
  }

  load(id: string): void {
    this.api.getDistributionVisit(id).subscribe({
      next: v => {
        this.visit.set(v);
        this.outcomeNotes = v.execution?.outcomeNotes ?? '';
        this.loading.set(false);
        this.loadLinkedOrders(id);
      },
      error: () => { this.errorMessage.set(this.t('sales.distribution.visits.errorLoad')); this.loading.set(false); }
    });
  }

  loadLinkedOrders(visitPlanId: string): void {
    this.api.listOrders({ visitPlanId, page: 1, pageSize: 50 }).subscribe({
      next: page => this.linkedOrders.set(page.items),
      error: () => this.linkedOrders.set([])
    });
  }

  loadCustomerNames(): void {
    this.platformApi.listAccountingCustomers().subscribe({
      next: customers => {
        const lookup: Record<string, string> = {};
        for (const customer of customers) {
          const name = customer.displayName?.trim() || customer.code?.trim() || customer.key;
          lookup[customer.key] = name;
          lookup[customer.key.toLowerCase()] = name;
        }
        this.customerNamesById.set(lookup);
      },
      error: () => this.customerNamesById.set({})
    });
  }

  customerLabel(customerAccountId: string): string {
    const lookup = this.customerNamesById();
    const normalized = customerAccountId.trim().toLowerCase();
    return lookup[normalized] ?? lookup[customerAccountId] ?? customerAccountId;
  }

  visitStatusLabel(status: string): string {
    return this.localizeStatus('sales.distribution.visits.status', status);
  }

  executionStatusLabel(status: string): string {
    return this.localizeStatus('sales.distribution.visits.executionStatus', status);
  }

  canStart(): boolean {
    const ex = this.visit()?.execution;
    return !ex || ex.status === 'NotStarted';
  }

  canComplete(): boolean {
    return this.visit()?.execution?.status === 'InProgress';
  }

  start(): void {
    const id = this.visit()?.id;
    if (!id) return;
    this.api.startVisitExecution(id).subscribe({
      next: v => this.visit.set(v),
      error: () => this.errorMessage.set(this.t('sales.distribution.visits.errorStart'))
    });
  }

  complete(): void {
    const id = this.visit()?.id;
    if (!id) return;
    this.api.completeVisitExecution(id, { outcomeNotes: this.outcomeNotes || null }).subscribe({
      next: v => this.visit.set(v),
      error: () => this.errorMessage.set(this.t('sales.distribution.visits.errorComplete'))
    });
  }

  captureProof(): void {
    const id = this.visit()?.id;
    if (!id) return;
    const payloadCanonical = `visit-proof:${id}:${Date.now()}`;
    this.api.createProofOfVisit({ visitPlanId: id, payloadCanonical, notes: this.proofNotes || null }).subscribe({
      next: () => this.api.listProofsOfVisit(id).subscribe({ next: p => this.proofs.set(p) }),
      error: () => this.errorMessage.set(this.t('sales.distribution.proofs.error'))
    });
  }

  submitAudit(): void {
    const id = this.visit()?.id;
    if (!id) return;
    this.api.createRetailAudit({
      visitPlanId: id,
      auditType: this.auditForm.auditType,
      complianceScore: this.auditForm.complianceScore,
      notes: this.auditForm.notes || null
    }).subscribe({
      next: () => this.api.listRetailAudits(id).subscribe({ next: a => this.audits.set(a) }),
      error: () => this.errorMessage.set(this.t('sales.distribution.retail.error'))
    });
  }

  private localizeStatus(prefix: string, status: string): string {
    if (!status) {
      return '-';
    }

    const key = `${prefix}.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.localization.translate(key);
    return translated === key ? status : translated;
  }

  t(key: string): string { return this.localization.translate(key); }
}
